import { useState, useEffect } from 'react';
import { Grid } from '@material-ui/core';
import { Option, Tabs, TextBlock, Toolbar, ToolbarButtonGroup } from 'neon-design-system';
import { PageHeader } from '../../../../components/PageHeader/PageHeader';
import { ToolbarRequestTypeButtonGroup } from '../../../../components/ToolbarRequestTypeButtonGroup/ToolbarRequestTypeButtonGroup';
import { RequestStatusType, RequestType } from '../../../../constants/request';
import { TabValue, Account } from '../../../../types/request';
import { ToolbarDatePicker } from '../../../../components/ToolbarDatePicker/ToolbarDatePicker';
import { ToolbarAccountSelect } from '../../../../components/ToolbarAccountSelect/ToolbarAccountSelect';
import { config } from '../../../../config';
import { useAccountChangeRequestsQuery } from '../../hooks/useAccountChangeRequestsQuery';

import { Loader } from '../../../../components/Loader/Loader';
import { ServerErrorView } from '../../../../components/ServerErrorView/ServerErrorView';
import { Range } from 'react-date-range';
import { useFilters } from '../../../../providers/FiltersProvider';
import { AccountChangeRequestsGrid, pageName } from '../../components/AccountChangeRequestsGrid/AccountChangeRequestsGrid';
import { ToolbarMenu } from '../../../../components/ToolbarMenu/ToolbarMenu';
import { REQUEST_STATUS_OPTIONS } from '../../../../constants/request';
import { NoAccountChangeRequestView } from '../../components/NoAccountChangeRequestView/NoAccountChangeRequestView';
import { PaginationGridWrapper } from '../../../../components/PaginationGridWrapper/PaginationGridWrapper';
import { useUpBreakpoints } from '../../../../providers/BreakpointsProvider';
import { toOption } from '../../../../utils/select';
import { MobileAccountChangeRequests } from './MobileAccountChangeRequests';
import { useAccountsQuery } from '../../../../hooks/useAccountsQuery';
import { debounceGetAccounts } from '../../../../hooks/useAccountsQuery';

export const activeFilterName = 'accountChangeRequests';

export const AccountChangeRequests = () => {
  const { filters, setFilters } = useFilters();
  const { data: accountsData, isFetching: accountsFetching } = useAccountsQuery();
  const accounts = accountsData || [];
  const { accountChangeRequests: accountChangeRequestsFilters } = filters;
  const [initLoading, setInitLoading] = useState<boolean>(true);
  const [showNoRequestsView, setShowNoRequestsView] = useState<boolean>(false);
  const [requestType, setRequestType] = useState<RequestType>(accountChangeRequestsFilters.requestType);
  const [requestStatus, setRequestStatus] = useState<RequestStatusType>(accountChangeRequestsFilters.requestStatus);
  const [tab, setTab] = useState<TabValue>(accountChangeRequestsFilters.tab);
  const defaultRanges = accountChangeRequestsFilters.ranges.map((range) => ({
    ...range,
    startDate: new Date(range.startDate!),
    endDate: new Date(range.endDate!)
  }));
  const { md } = useUpBreakpoints();

  const isMobile = !md;
  const [ranges, setRanges] = useState<Range[]>(defaultRanges);
  const [selectedAccounts, setSelectedAccounts] = useState<Option[] | null>(accountChangeRequestsFilters.selectedAccounts);
  const { data, isFetching, isError } = useAccountChangeRequestsQuery(initLoading && !isMobile ? undefined : activeFilterName, pageName);
  const options = accounts.map((account: Account) => toOption(account.id));
  const accountChangeRequests = data?.data || [];

  useEffect(() => {
    setFilters({
      ...filters,
      accountChangeRequests: {
        requestType,
        requestStatus,
        tab,
        ranges,
        selectedAccounts
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ranges, requestStatus, requestType, selectedAccounts, setFilters, tab]);

  useEffect(() => {
    if (!isFetching && !isError && initLoading) {
      if (!accountChangeRequests?.length) setShowNoRequestsView(true);
      setInitLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountChangeRequests, isFetching, isError]);

  if (isFetching && accountsFetching && initLoading) {
    return <Loader />;
  }

  if (isError) {
    return <ServerErrorView />;
  }

  if (showNoRequestsView) return <NoAccountChangeRequestView />;

  if (isMobile)
    return (
      <MobileAccountChangeRequests
        isFetching={isFetching}
        accountChangeRequests={accountChangeRequests}
        options={options}
        selectedAccounts={selectedAccounts}
        setSelectedAccounts={setSelectedAccounts}
        debounceGetAccounts={debounceGetAccounts}
      />
    );

  return (
    <Grid container spacing={4}>
      <Grid item xs={12}>
        <PageHeader
          title="Account Change Requests"
          buttonLabel="Navigate to Portfolio"
          onClick={() => (window.location.href = config.portfolioTradesPage)}
          belowHeading={
            <Tabs
              tabs={[
                { title: 'Latest activity', value: 'latest' },
                { title: 'All requests', value: 'all' }
              ]}
              value={tab}
              onChange={(v) => setTab(v as TabValue)}
            />
          }
          leftToolbar={
            tab === 'all' ? (
              <Toolbar>
                <ToolbarRequestTypeButtonGroup type={requestType} setRequestType={setRequestType} />
                <ToolbarButtonGroup>
                  <ToolbarMenu
                    selectedOption={requestStatus}
                    options={REQUEST_STATUS_OPTIONS}
                    onChange={(value) => setRequestStatus(value as RequestStatusType)}
                  />
                  <ToolbarDatePicker selectedRanges={ranges} onChange={(item) => setRanges([item.selection])} />
                </ToolbarButtonGroup>
              </Toolbar>
            ) : (
              <TextBlock styleName="footnote">Showing the most recent requests</TextBlock>
            )
          }
          rightToolbar={
            tab === 'all' && (
              <ToolbarAccountSelect loadOptions={debounceGetAccounts} options={options} selected={selectedAccounts} onChange={setSelectedAccounts} />
            )
          }
        />
      </Grid>
      <Grid item xs={12}>
        {isFetching ? (
          <Loader fixedHeight />
        ) : (
          <PaginationGridWrapper
            activeFilterName={activeFilterName}
            hidePagination={tab === 'latest'}
            rowData={tab === 'latest' ? accountChangeRequests.slice(0, 15) : accountChangeRequests}
            GridComponent={AccountChangeRequestsGrid}
          />
        )}
      </Grid>
    </Grid>
  );
};

export default AccountChangeRequests;
